/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getSurveys,
    getPremadeSurveys,
    updateSurvey,
    createSurvey,
    createSurveyQuestion,
    updateSurveyQuestion,
    getSurveyBySlug,
    submitSurveyResponse,
    getSurveySubmissions,
    getSurveyStatistics,
    downloadSurveyStatistics,
    duplicateSurvey,
    createSurveyFromPremade,
    deleteSurvey,
    analyzeSurveyWithAI,
} from './surveyApi';

import {
    logoutUser,
} from '../user/userApi';

import { Survey, SurveyState, SurveySubmission, SurveyStatistics } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isCreatingFromPremade: false,
    surveys: [],
    premadeSurveys: [],
    singleSurvey: null,
    surveySubmissions: [],
    singleSurveySubmission: null,
    surveyStatistics: null,
    isSurveyCreated: false,
    hasSubmitted: false,
} as SurveyState;

export const surveySlice = createSlice({
    name: 'survey',
    initialState,
    reducers: {
        loadSingleSurvey: (state, action: PayloadAction<number>) => {
            const survey = state.surveys.find(s => s.id === action.payload) || null;
            state.singleSurvey = survey;
            state.surveyStatistics = null;
            state.surveySubmissions = [];
            state.singleSurveySubmission = null;
        },
        loadSinglePremadeSurvey: (state, action: PayloadAction<number>) => {
            const survey = state.premadeSurveys.find(s => s.id === action.payload) || null;
            state.singleSurvey = survey;
            state.surveyStatistics = null;
            state.surveySubmissions = [];
            state.singleSurveySubmission = null;
        },
        resetSurveyCreated: (state) => {
            state.isSurveyCreated = false;
        },
        setSingleSurveySubmission: (state, action: PayloadAction<number>) => {
            const submission = state.surveySubmissions?.find(s => s.id === action.payload) || null;
            state.singleSurveySubmission = submission;
        },
        clearSingleSurveySubmission: (state) => {
            state.singleSurveySubmission = null;
        },
    },
    extraReducers: (builder) => {
        // Update user
        builder
            .addCase(getSurveys.fulfilled, (state:SurveyState, action) => {
                state.isFetching = false;
                state.surveys = action?.payload?.surveys;
                // notification.success({
                //     message: 'Success',
                //     description: 'Duomenys sėkmingai atnaujinti.',
                // });
            });

        builder
            .addCase(getPremadeSurveys.fulfilled, (state:SurveyState, action) => {
                state.isFetching = false;
                state.premadeSurveys = action?.payload?.surveys;
                // notification.success({
                //     message: 'Success',
                //     description: 'Duomenys sėkmingai atnaujinti.',
                // });
            });

        builder.addCase(getSurveyBySlug.fulfilled, (state:SurveyState, action) => {
            state.isFetching = false;
            state.singleSurvey = action?.payload?.survey;
            state.hasSubmitted = action?.payload?.hasSubmitted;
        });

        // Update survey
        builder.addCase(updateSurvey.fulfilled, (state: SurveyState, action) => {
            state.isFetching = false;
            const updatedSurvey = action?.payload?.survey;

            // Update singleSurvey
            state.singleSurvey = updatedSurvey;

            // Update surveys array
            const index = state.surveys.findIndex(s => s.id === updatedSurvey.id);
            if (index !== -1) {
                state.surveys[index] = updatedSurvey;
            }

            notification.success({
                message: 'Pavyko!',
                description: 'Apklausa sėkmingai atnaujinta',
            });
        });

        // Analyze survey with AI
        builder.addCase(analyzeSurveyWithAI.fulfilled, (state: SurveyState, action) => {
            state.isFetching = false;
            const updatedSurvey = action.payload.survey;

            // Update singleSurvey with the complete survey object from server
            state.singleSurvey = updatedSurvey;

            // Update surveys array
            const index = state.surveys.findIndex(s => s.id === updatedSurvey.id);
            if (index !== -1) {
                state.surveys[index] = updatedSurvey;
            }

            // Update premadeSurveys array
            const premadeIndex = state.premadeSurveys.findIndex(s => s.id === updatedSurvey.id);
            if (premadeIndex !== -1) {
                state.premadeSurveys[premadeIndex] = updatedSurvey;
            }

            notification.success({
                message: 'AI analizė baigta!',
                description: 'Apklausos duomenys sėkmingai išanalizuoti dirbtiniu intelektu',
            });
        });

        builder.addCase(createSurvey.fulfilled, (state:SurveyState, action) => {
            const createdSurvey = action?.payload?.survey;

            // Update singleSurvey
            state.singleSurvey = createdSurvey;
            state.isFetching = false;
            state.isSurveyCreated = true;

            // Add to appropriate array based on is_premade
            if (createdSurvey.is_premade === 1) {
                state.premadeSurveys.push(createdSurvey);
            } else {
                state.surveys.push(createdSurvey);
            }

            notification.success({
                message: 'Pavyko!',
                description: 'Apklausa sėkmingai sukurta',
            });
        });

        builder.addCase(createSurveyQuestion.fulfilled, (state: SurveyState, action) => {
            const createdQuestion = action?.payload?.question;
            const surveyId = createdQuestion?.survey_id;

            // Check in regular surveys
            const surveyIndex = state.surveys.findIndex(s => s.id === surveyId);
            if (surveyIndex !== -1) {
                const survey = state.surveys[surveyIndex];
                if (survey) {
                    const updatedSurvey: Survey = {
                        ...survey,
                        questions: [...(survey.questions || []), createdQuestion],
                    };

                    state.surveys[surveyIndex] = updatedSurvey;

                    if (state.singleSurvey && state.singleSurvey.id === surveyId) {
                        state.singleSurvey = updatedSurvey;
                    }

                    notification.success({
                        message: 'Pavyko!',
                        description: 'Klausimas sėkmingai sukurtas',
                    });
                }
            } else {
                // Check in premade surveys
                const premadeSurveyIndex = state.premadeSurveys.findIndex(s => s.id === surveyId);
                if (premadeSurveyIndex !== -1) {
                    const survey = state.premadeSurveys[premadeSurveyIndex];
                    if (survey) {
                        const updatedSurvey: Survey = {
                            ...survey,
                            questions: [...(survey.questions || []), createdQuestion],
                        };

                        state.premadeSurveys[premadeSurveyIndex] = updatedSurvey;

                        if (state.singleSurvey && state.singleSurvey.id === surveyId) {
                            state.singleSurvey = updatedSurvey;
                        }

                        notification.success({
                            message: 'Pavyko!',
                            description: 'Klausimas sėkmingai sukurtas',
                        });
                    }
                }
            }
            state.isFetching = false;
        });

        builder.addCase(updateSurveyQuestion.fulfilled, (state: SurveyState, action) => {
            const updatedQuestion = action?.payload?.question;
            const surveyId = updatedQuestion?.survey_id;

            // Check in regular surveys
            const surveyIndex = state.surveys.findIndex(s => s.id === surveyId);
            if (surveyIndex !== -1) {
                const survey = state.surveys[surveyIndex];
                if (survey) {
                    const updatedQuestions = survey.questions.map(q => (q.id === updatedQuestion.id ? updatedQuestion : q));

                    const updatedSurvey: Survey = {
                        ...survey,
                        questions: updatedQuestions,
                    };

                    state.surveys[surveyIndex] = updatedSurvey;

                    if (state.singleSurvey && state.singleSurvey.id === surveyId) {
                        state.singleSurvey = updatedSurvey;
                    }

                    notification.success({
                        message: 'Pavyko!',
                        description: 'Klausimas sėkmingai atnaujintas',
                    });
                }
            } else {
                // Check in premade surveys
                const premadeSurveyIndex = state.premadeSurveys.findIndex(s => s.id === surveyId);
                if (premadeSurveyIndex !== -1) {
                    const survey = state.premadeSurveys[premadeSurveyIndex];
                    if (survey) {
                        const updatedQuestions = survey.questions.map(q => (q.id === updatedQuestion.id ? updatedQuestion : q));

                        const updatedSurvey: Survey = {
                            ...survey,
                            questions: updatedQuestions,
                        };

                        state.premadeSurveys[premadeSurveyIndex] = updatedSurvey;

                        if (state.singleSurvey && state.singleSurvey.id === surveyId) {
                            state.singleSurvey = updatedSurvey;
                        }

                        notification.success({
                            message: 'Pavyko!',
                            description: 'Klausimas sėkmingai atnaujintas',
                        });
                    }
                }
            }
            state.isFetching = false;
        });

        builder.addCase(submitSurveyResponse.fulfilled, (state: SurveyState) => {
            state.isFetching = false;
            state.hasSubmitted = true;
            notification.success({
                message: 'Pavyko!',
                description: 'Atsakymai sėkmingai pateikti',
            });
        });

        builder.addCase(getSurveySubmissions.fulfilled, (state: SurveyState, action) => {
            state.isFetching = false;
            const payload = action?.payload as { submissions?: SurveySubmission[] };
            state.surveySubmissions = payload?.submissions || [];
        });

        builder.addCase(getSurveyStatistics.fulfilled, (state: SurveyState, action) => {
            state.isFetching = false;
            const payload = action?.payload as { data?: SurveyStatistics };
            state.surveyStatistics = payload?.data || null;
        });

        builder.addCase(downloadSurveyStatistics.fulfilled, (state: SurveyState) => {
            state.isFetching = false;
        });

        builder.addCase(duplicateSurvey.fulfilled, (state: SurveyState, action) => {
            const duplicatedSurvey = action?.payload?.survey;

            // Update singleSurvey
            state.singleSurvey = duplicatedSurvey;
            state.isFetching = false;
            state.isSurveyCreated = true;

            // Add to appropriate array based on is_premade
            if (duplicatedSurvey.is_premade === 1) {
                state.premadeSurveys.push(duplicatedSurvey);
            } else {
                state.surveys.push(duplicatedSurvey);
            }

            notification.success({
                message: 'Pavyko!',
                description: 'Apklausa sėkmingai nukopijuota',
            });
        });

        builder.addCase(createSurveyFromPremade.fulfilled, (state: SurveyState, action) => {
            const createdSurvey = action?.payload?.survey;

            state.singleSurvey = createdSurvey;
            state.isFetching = false;
            state.isCreatingFromPremade = false;
            // Do not flip isSurveyCreated here to avoid redirecting to edit page

            // Always add to regular surveys list
            state.surveys.push(createdSurvey);

            notification.success({
                message: 'Pavyko!',
                description: 'Apklausa sukurta iš paruoštos apklausos',
            });
        });

        builder.addCase(deleteSurvey.fulfilled, (state: SurveyState, action) => {
            const { surveyId } = action.payload;

            // Remove survey from both arrays
            state.surveys = state.surveys.filter(survey => survey.id !== surveyId);
            state.premadeSurveys = state.premadeSurveys.filter(survey => survey.id !== surveyId);

            // Clear singleSurvey if it was the deleted one
            if (state.singleSurvey?.id === surveyId) {
                state.singleSurvey = null;
            }

            state.isFetching = false;

            notification.success({
                message: 'Pavyko!',
                description: 'Apklausa sėkmingai ištrinta',
            });
        });

        // on logout
        builder
            .addCase(logoutUser.fulfilled, (state:SurveyState) => {
                state.surveys = [];
                state.premadeSurveys = [];
                state.singleSurvey = null;
                state.surveySubmissions = [];
                state.singleSurveySubmission = null;
                state.surveyStatistics = null;
                state.isSurveyCreated = false;
            });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                updateSurvey.pending,
                createSurvey.pending,
                createSurveyQuestion.pending,
                updateSurveyQuestion.pending,
                duplicateSurvey.pending,
                deleteSurvey.pending,
                analyzeSurveyWithAI.pending,
            ),
            (state:SurveyState) => {
                state.isFetching = true;
                state.error = null;
                state.isSurveyCreated = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                createSurveyFromPremade.pending,
            ),
            (state:SurveyState) => {
                state.isFetching = true;
                state.isCreatingFromPremade = true;
                state.error = null;
                state.isSurveyCreated = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getSurveys.pending,
                getPremadeSurveys.pending,
                getSurveyBySlug.pending,
                submitSurveyResponse.pending,
                getSurveySubmissions.pending,
                getSurveyStatistics.pending,
                downloadSurveyStatistics.pending,
                duplicateSurvey.pending,
                deleteSurvey.pending,
            ),
            (state:SurveyState) => {
                state.isFetching = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                updateSurvey.rejected,
                getSurveys.rejected,
                getPremadeSurveys.rejected,
                createSurvey.rejected,
                createSurveyQuestion.rejected,
                updateSurveyQuestion.rejected,
                getSurveyBySlug.rejected,
                submitSurveyResponse.rejected,
                getSurveySubmissions.rejected,
                getSurveyStatistics.rejected,
                downloadSurveyStatistics.rejected,
                duplicateSurvey.rejected,
                deleteSurvey.rejected,
                analyzeSurveyWithAI.rejected,
            ),
            (state:SurveyState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createSurveyFromPremade.rejected,
            ),
            (state:SurveyState, action) => {
                state.isFetching = false;
                state.isCreatingFromPremade = false;
                state.error = action.error;
            },
        );
    },
});

export const { loadSingleSurvey, loadSinglePremadeSurvey, resetSurveyCreated, setSingleSurveySubmission, clearSingleSurveySubmission } = surveySlice.actions;
export default surveySlice.reducer;
